import React, {useEffect, useMemo, useRef, useState} from "react";
import getTemplatesAnalyzer from "../../api/getTemplatesAnalyzer";
import getConversionsOverTime from "../../api/getConversionsOverTime";
import getTotalRecovered from "../../api/getTotalRecovered";
import {getTemplates} from "../../api/getTemplates";
import {editNotification} from "../../api/editNotification";
import {deleteNotification} from "../../api/deleteNotification";
import useUserContext from "../../hooks/useUserContext";
import {sendNotificationPreview} from "../../api/sendNotificationPreview";
import {createNotification} from "../../api/createNotification";
import {PAGE_SIZE} from "../../consts/pageSize";
import getDashboardBoxes from "../../api/getDashboardBoxes";
import {useLocation, useNavigate} from "react-router-dom";
import {getLastDaysMessages} from "../../api/getLastDaysMessages";
import {getLastDaysCheckouts} from "../../api/getLastDaysCheckouts";
import useDataResource from "../../hooks/useDataResource";
import {AppContextProps, AppContextProviderProps, PageDataProps} from "./AppContextTypes";
import {defaultAppContextValue} from "./DefaultAppContext";
import {DataSourceDependencies} from "./DataSourceDependencies";
import {getLastDaysOrders} from "../../api/getLastDaysOrders";
import {getWabaId} from "../../api/getWabaId";
import {getBlackoutPeriod} from "../../api/getBlackoutPeriods";
import {getShop} from "../../api/getShop";
import {getShopifyShopBillingUsage} from "../../api/getShopifyShopBillingDetails";
import {getConversations} from "../../api/getConversations";
import SocketService from "../../SocketServer";
import {SOCKET_MESSAGE_TYPES, SocketMessageProps} from "../../types/socketMessageProps";
import {getConversationMessages} from "../../api/getConversationMessages";
import {ConversationMessage} from "../../types/ConversationMessage";
import useToast from "../../hooks/useToast";
import {ToastSkin, ToastType} from "../ToastContext";
import {ChatIcon} from "../../assets/icons/ChatIcon";
import {getFlows} from "../../api/getFlows";
import {getFlowTypes} from "../../api/getFlowTypes";
import {getFlowTriggers} from "../../api/getFlowTriggers";
import {FlowProps} from "../../types/FlowProps";
import {createFlow} from "../../api/createFlow";
import {getCampaigns} from "../../api/getCampaigns";
import {createCampaign} from "../../api/createCampaign";
import {getShopClients} from "../../api/getShopClients";
import {getSegments} from "../../api/getSegments";
import getCheckoutRecoverableVsRecovered from "../../api/getCheckoutRecoverableVsRecovered";
import getDashboardCampaignBoxes from "../../api/getDashboardCampaignBoxes";
import {getCampaignsAvg} from "../../api/getCampaignsAvg";
import {getLastDaysCampaigns} from "../../api/getLastDaysCampaigns";
import {isDevelopment} from "../../utils/isDevelopment";
import {getTwilioSender} from "../../api/getTwilioSender";
import {SocketServiceV2} from "../../SocketServerV2";
import {syncConversationsFromSyncTime} from "../../api/syncConversationsFromSyncTime";
import {getProductDiscounts} from "../../api/getProductDiscounts";

export const AppContext = React.createContext<AppContextProps>(defaultAppContextValue);

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
    const [days, setDays] = useState(30);
    const templatesAnalyzer = useDataResource();
    const conversionsOverTime = useDataResource();
    const checkoutRecoverableVsRecovered = useDataResource();
    const totalRecovered = useDataResource();
    const abandonedCheckouts = useDataResource();
    const dashboardBoxes = useDataResource();
    const lastDaysMessages = useDataResource();
    const lastDaysCheckouts = useDataResource();
    const lastDaysOrders = useDataResource();
    const lastDaysCampaigns = useDataResource();
    const campaignBoxes = useDataResource();
    const campaignsAvg = useDataResource();
    const orders = useDataResource();
    const flows = useDataResource();
    const flowTypes = useDataResource();
    const flowTriggers = useDataResource();
    const templates = useDataResource();
    const campaigns = useDataResource();
    const waba = useDataResource();
    const shop = useDataResource();
    const blackoutPeriods = useDataResource();
    const shopifyBillingDetails = useDataResource();
    const conversations = useDataResource();
    const segments = useDataResource();
    const shopClients = useDataResource();
    const discounts= useDataResource();
    const conversationMessagesMap = useRef<Map<number, ConversationMessage[]>>(new Map());
    const [conversationMessagesForceRender, setConversationMessagesForceRender] = useState(0);
    const conversationMessagesForceRenderRef = useRef(0);
    const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
    const { selectedDomain } = useUserContext();
    const [isDashboardLoaded, setIsDashboardLoaded] = useState(false);
    const [totalUnseenMessages, setTotalUnseenMessages] = useState(0);
    const totalUnseenMessagesRef = useRef(0);
    const [hasUserInteract, setHasUserInteract] = useState(false);
    const [isChatSocketReady, setIsChatSocketReady] = useState(false);
    const { showToast, closeToast } = useToast();
    const navigate = useNavigate();
    const currentConversationIdRef = useRef<number | null>(null);

    const [socket, setSocket] = useState<SocketServiceV2 | null>(null);
    const { pathname } = useLocation();

    const phone = useMemo(() => waba?.data?.waba?.phones?.[0], [waba]);

    let socketInstance = useRef<SocketServiceV2 | null>(null);

    useEffect(() => {
        loadShopifyBillingDetails();
    }, [selectedDomain, shop.data.platformId]);

    useEffect(() => {
        if(conversations.isLoaded) {
            setTotalUnseenMessages(conversations.data.reduce((total: number, conversation: any) => total + conversation.unreadMessagesCount, 0));
        }
    }, [conversations.isLoaded]);

    useEffect(() => {
        currentConversationIdRef.current = currentConversationId;
    }, [currentConversationId]);

    useEffect(() => {
        conversationMessagesForceRenderRef.current = conversationMessagesForceRender;
    }, [conversationMessagesForceRender]);

    useEffect(() => {
        totalUnseenMessagesRef.current = totalUnseenMessages;
    }, [totalUnseenMessages]);

    const loadFlows = async () => {
        if(!selectedDomain) return;
        flowTypes.load(getFlowTypes(selectedDomain));
        flowTriggers.load(getFlowTriggers(selectedDomain));
        flows.load(getFlows(selectedDomain));
    }

    const loadTemplates = async () => {
        if(!selectedDomain) return;
        templates.load(getTemplates(selectedDomain));
    }

    const loadCampaigns = async () => {
        if(!selectedDomain) return;
        campaigns.load(getCampaigns(selectedDomain));
        lastDaysCampaigns.load(getLastDaysCampaigns(selectedDomain));
    }

    const loadConversations = async () => {
        if(!selectedDomain) return;
        conversations.load(getConversations(selectedDomain));
    }

    const loadSegments = async () => {
        if(!selectedDomain) return;
        segments.load(getSegments(selectedDomain));
    }

    const loadShopClients = async () => {
        if(!selectedDomain) return;
        shopClients.load(getShopClients(selectedDomain));
    }

    const loadDiscounts = async () => {
        if(!selectedDomain) return;
        discounts.load(getProductDiscounts(selectedDomain));
    }

    const loadConversationMessages = async (conversationId: number) => {
        if(!selectedDomain) return;
        const newConversation = await getConversationMessages(selectedDomain, conversationId);
        conversationMessagesMap.current.set(conversationId, newConversation.data as ConversationMessage[]);
        setConversationMessagesForceRender(conversationMessagesForceRenderRef.current + 1);
    }

    const loadWaba = async () => {
        if(!selectedDomain) return;
        waba.load(getWabaId(selectedDomain));
    }

    const loadShop = async () => {
        if(!selectedDomain) return;
        shop.load(getShop(selectedDomain));
    }

    const loadMessages = async () => {
        if(!selectedDomain) return;
        lastDaysMessages.load(getLastDaysMessages(selectedDomain));
    }

    const loadCheckouts = async () => {
        if(!selectedDomain) return;
        lastDaysCheckouts.load(getLastDaysCheckouts(selectedDomain));
    }

    const loadOrders = async () => {
        if(!selectedDomain) return;
        lastDaysOrders.load(getLastDaysOrders(selectedDomain));
    }

    const loadBlackoutPeriods = async () => {
        if(!selectedDomain) return;
        blackoutPeriods.load(getBlackoutPeriod(selectedDomain));
    }

    const loadShopifyBillingDetails = async () => {
        if(!selectedDomain || shop.data.platformId !== 1) {
            return;
        }
        const details = getShopifyShopBillingUsage(selectedDomain);
        shopifyBillingDetails.load(details);
    }

    const loadNext = async (source: PageDataProps, getNextData: (selectedDomain: string, nextPage: number) => any): Promise<PageDataProps> => {
        if(!selectedDomain) return source;
        const nextPage = source.page + 1;
        const nextResults = await getNextData(selectedDomain, nextPage);
        const nextData = { ...source.data, ...nextResults.data };
        const nextIsLoaded = nextResults.data.length < PAGE_SIZE;
        return { data: nextData, page: nextPage, isFullyLoaded: nextIsLoaded }
    }

    const loadMore = async (source: string) => {
        if(!selectedDomain) return;
    }

    const clearData = () => {
        setIsDashboardLoaded(false);
        flows.clear();
        templates.clear();
        orders.clear();
        templatesAnalyzer.clear();
        conversionsOverTime.clear();
        checkoutRecoverableVsRecovered.clear();
        totalRecovered.clear();
        dashboardBoxes.clear();
        abandonedCheckouts.clear();
    }

    const removeFlow = (id: number) => {
        flows.setData(flows.data.filter((flow: any) => flow.id !== id));
    }

    const removeTemplate = (id: number) => {
        templates.setData(templates.data.filter((template: any) => template.id !== id));
        flows.setData(flows.data.map((flow: FlowProps) => {
            return {
                ...flow,
                templates: flow.templates?.filter(template => template.id !== id)
            };
        }))
    }

    const loadDashboardResources = async () => {
        if(!selectedDomain) return;
        templatesAnalyzer.load(getTemplatesAnalyzer(days, selectedDomain));
        conversionsOverTime.load(getConversionsOverTime(days, selectedDomain));
        checkoutRecoverableVsRecovered.load(getCheckoutRecoverableVsRecovered(days, selectedDomain));
        totalRecovered.load(getTotalRecovered(days, selectedDomain));
        dashboardBoxes.load(getDashboardBoxes(days, selectedDomain));
        campaignBoxes.load(getDashboardCampaignBoxes(days, selectedDomain));
        campaignsAvg.load(getCampaignsAvg(days, selectedDomain));
        setIsDashboardLoaded(true);
    }

    const lazyLoadData = (forceUpdate: boolean = false) => {
        const cleanedPathname = pathname.replace(/\/\d+(?=\/|$)/, '');
        DataSourceDependencies.dashboardRoutes.includes(cleanedPathname) && (forceUpdate || !isDashboardLoaded) && loadDashboardResources();
        DataSourceDependencies.flowsRoutes.includes(cleanedPathname) && (forceUpdate || !flows.isLoaded) && loadFlows();
        (forceUpdate || !templates.isLoaded) && loadTemplates();
        DataSourceDependencies.campaignsRoutes.includes(cleanedPathname) && (forceUpdate || !campaigns.isLoaded) && loadCampaigns();
        DataSourceDependencies.wabaRoutes.includes(cleanedPathname) && (forceUpdate || !waba.isLoaded) && loadWaba();
        DataSourceDependencies.messagesRoutes.includes(cleanedPathname) && (forceUpdate || !lastDaysMessages.isLoaded) && loadMessages();
        DataSourceDependencies.checkoutsRoutes.includes(cleanedPathname) && (forceUpdate || !lastDaysCheckouts.isLoaded) && loadCheckouts();
        DataSourceDependencies.ordersRoutes.includes(cleanedPathname) && (forceUpdate || !orders.isLoaded) && loadOrders();
        DataSourceDependencies.blackoutPeriodRoutes.includes(cleanedPathname) && (forceUpdate || !blackoutPeriods.isLoaded) && loadBlackoutPeriods();
        DataSourceDependencies.conversations.includes(cleanedPathname) && (forceUpdate || !conversations.isLoaded) && loadConversations();
        DataSourceDependencies.segments.includes(cleanedPathname) && (forceUpdate || !segments.isLoaded) && loadSegments();
        // DataSourceDependencies.shopClients.includes(cleanedPathname) && (forceUpdate || !shopClients.isLoaded) && loadShopClients();
        (forceUpdate || !discounts.isLoaded) && loadDiscounts();
    }

    const setBalance = (newBalance: number) => {
        shop.data.balance = newBalance;
    }

    const setConversationAsSeen = (conversationId: number) => {
        const nextConversations = conversations.data.map((conversation: any) => {
            if(conversation.id === conversationId) {
                const currentConversationUnseen = conversation.unreadMessagesCount;
                setTotalUnseenMessages(Math.max(totalUnseenMessages - currentConversationUnseen, 0));
                return { ...conversation, unreadMessagesCount: 0 }
            } else {
                return conversation;
            }
        });
        conversations.setData(nextConversations);
    }

    const addMessageToConversation = (newMessage: ConversationMessage, tempId: string | undefined = undefined) => {
        if(newMessage.conversationId) {
            const suppressShopAlerts = !!newMessage.metadataJson?.suppressShopAlerts;
            const conversation = conversationMessagesMap.current.get(newMessage.conversationId);
            if(conversation) {
                if(!conversation.find(conversationMessage => conversationMessage.id === newMessage.id)) {
                    conversation.unshift(newMessage);
                    conversationMessagesMap.current.set(newMessage.conversationId, conversation);
                    // setConversationMessagesForceRender(conversationMessagesForceRender + 1);
                }
            }

            const newConversations = conversations.data.map((conversation: any) => {
                if(conversation.id === newMessage.conversationId) {
                    if(currentConversationId !== conversation?.id && !suppressShopAlerts) {
                        setTotalUnseenMessages(totalUnseenMessages + 1);
                    }
                    return {
                        ...conversation,
                        unreadMessagesCount: suppressShopAlerts ? conversation.unreadMessagesCount : (currentConversationId === conversation?.id ? 0 : conversation.unreadMessagesCount + 1),
                        activationWindowDeadline: newMessage.sentByClient ? new Date(new Date(newMessage.time).getTime() + (24 * 60) * 60 * 1000) : conversation.activationWindowDeadline,
                        content: newMessage.content,
                        time: newMessage.time,
                        sentByClient: newMessage.sentByClient,
                        status: newMessage.status,
                    }
                }
                return conversation;
            });
            conversations.setData(newConversations);
            try {
                if(newMessage.sentByClient && pathname !== '/conversations' && !suppressShopAlerts) {
                    showToast({
                        type: ToastType.notification,
                        title: newMessage.fromName ? `${newMessage.fromName}:` : '',
                        icon: <ChatIcon width={36} color='rgba(255,255,255,0.2)' />,
                        text: newMessage.content,
                        skin: ToastSkin.neutral,
                        displayTime: 8000,
                        buttons: [{ text: 'Go to Conversation', onClick: () => {
                            navigate('/conversations');
                            closeToast();
                        }}],
                    })
                }
                newMessage.sentByClient && hasUserInteract && !suppressShopAlerts && playNotification();
            } catch(error) {
                console.log("error2: ", error);
            }
        }
    }

    const playNotification = () => {
        try {
            const audio: HTMLAudioElement | null = document.getElementById('notification-audio-player') as HTMLAudioElement | null;
            audio && audio.play();
        } catch(error) {
            console.log("error: ", error);
        }
    }

    const updateConversationStatus = (conversationId: number, messageExternalId: string, newStatus: string) => {
        if(newStatus) {
            const currentConversationMessages = conversationMessagesMap.current.get(conversationId);
            if(currentConversationMessages) {
                const nextConversationMessages = currentConversationMessages.map((message: ConversationMessage) => {
                    if (message.status !== 'READ' && message.externalId === messageExternalId) {
                        return {...message, status: newStatus};
                    } else {
                        return message;
                    }
                });
                conversationMessagesMap.current.set(conversationId, nextConversationMessages);
                setConversationMessagesForceRender(conversationMessagesForceRender + 1);
            }
        }
    }

    const updateInteraction = () => {
        setHasUserInteract(true);
        document.removeEventListener('click', updateInteraction);
    }

    useEffect(() => {
        document.addEventListener('click', updateInteraction);

        return () => {
            document.removeEventListener('click', updateInteraction);
        }
    }, []);

    useEffect(() => {
        lazyLoadData();
    }, [pathname, selectedDomain]);

    useEffect(() => {
        selectedDomain && loadShop();
    }, [selectedDomain]);

    useEffect(() => {
        setIsDashboardLoaded(false);
        lazyLoadData(true);
    }, [days]);

    useEffect(() => {
        clearData();
        lazyLoadData(true);
    }, [selectedDomain]);

    useEffect(() => {
        if(!selectedDomain) return;
        if(socket) {
            socket.close();
        }
        socketInstance.current = new SocketServiceV2(selectedDomain, 5000, 10, (event: any) => null, () => setIsChatSocketReady(true), {
            getCursor: () => localStorage.getItem('cursor:global') || '',
            syncSince: async (cursor) => {
                const downTimeMessages = await syncConversationsFromSyncTime(selectedDomain, cursor);
                let newUnseenMessages = 0;
                if(downTimeMessages.data.length) {
                    loadConversations();
                    const conversationIdsToReload: number[] = [];
                    downTimeMessages.data?.forEach((message: ConversationMessage) => {
                        if(message.conversationId && !conversationIdsToReload.includes(message.conversationId)) {
                            if(pathname === '/conversations' && currentConversationIdRef.current && message.conversationId === currentConversationIdRef.current) {
                                loadConversationMessages(currentConversationIdRef.current);
                            } else {
                                conversationMessagesMap.current.delete(message.conversationId);
                            }
                            conversationIdsToReload.push(message.conversationId);
                        }
                        if(pathname !== '/conversations' && message.conversationId !== currentConversationIdRef.current && !message.metadataJson?.suppressShopAlerts) {
                            newUnseenMessages++;
                        }
                    });
                    setTotalUnseenMessages(totalUnseenMessagesRef.current + newUnseenMessages);
                }
                localStorage.setItem('cursor:global', (new Date()).toString());
            },
        });
        setSocket(socketInstance.current);

        return () => socketInstance.current?.close();
    }, [selectedDomain]);

    useEffect(() => {
        socketInstance.current?.setCallback((message: SocketMessageProps) => {
            isDevelopment() && console.log("in socket callback 1", message?.shop, selectedDomain);
            // console.log("conversations: ", conversations.data);
            if(message?.shop !== selectedDomain) return;
            isDevelopment() && console.log("in socket callback 2");
            if(message.type === SOCKET_MESSAGE_TYPES.STATUS_UPDATE) {
                isDevelopment() && console.log("in socket callback 3");
                conversations.data.map((conversation: any) => {
                    if (conversation.phone === message.payload?.fromPhoneNumber && message.payload?.externalId && message.payload.nextStatus) {
                        updateConversationStatus(conversation.id, message.payload?.externalId, message.payload.nextStatus);
                    }
                });
            }

            if(message.type === SOCKET_MESSAGE_TYPES.NEW_MESSAGE) {
                isDevelopment() && console.log("in socket callback 4: ", message.payload);
                const time = message.payload?.time;
                time && localStorage.setItem('cursor:global', time.toString());
                const exist = conversations.data?.find((con: any) => {
                    return con.id === message.payload?.conversationId;
                })
                if(exist) {
                    addMessageToConversation(message.payload as ConversationMessage);
                } else {
                    loadConversations();
                }
            }

            // Send "read" indication to the server
            // socketInstance.sendMessage('read', { messageId: message.id });
        });
    }, [conversations, conversationMessagesForceRender, selectedDomain, hasUserInteract]);

    return (
        <AppContext.Provider value={{
            days,
            actions: {
                setDays,
                sendNotificationPreview,
                createNotification,
                createFlow,
                createCampaign,
                editNotification,
                deleteNotification,
                loadFlows,
                loadTemplates,
                loadCampaigns,
                loadWaba,
                loadShop,
                loadShopClients,
                loadMore,
                clearData,
                removeFlow,
                removeTemplate,
                loadBlackoutPeriods,
                setBalance,
                loadShopifyBillingDetails,
                loadConversationMessages,
                addMessageToConversation,
                setConversationAsSeen,
                setCurrentConversationId,
                loadSegments,
                loadDiscounts,
            },
            data: {
                abandonedCheckouts,
                orders,
                flows,
                flowTypes,
                flowTriggers,
                templates,
                campaigns,
                waba,
                phone,
                shop,
                conversations,
                conversationMessages: conversationMessagesMap.current,
                templatesAnalyzer,
                conversionsOverTime,
                checkoutRecoverableVsRecovered,
                totalRecovered,
                dashboardBoxes,
                campaignBoxes,
                campaignsAvg,
                lastDaysMessages,
                lastDaysCheckouts,
                lastDaysOrders,
                lastDaysCampaigns,
                blackoutPeriods,
                shopifyBillingDetails,
                totalUnseenMessages,
                segments,
                shopClients,
                isChatSocketReady,
                discounts,
            }
        }}>
            {children}
        </AppContext.Provider>
    );
}
